import {LanguageStore}                                                        from '../../stores/translation/language.store';
import {
  SettingsStore
}                                                                             from '../../stores/base-data/settings.store';
import {GetRoundNumericPipe}                                                  from '../../ap-utils';
import {
  ApTranslationService
}                                                                             from '../../ap-utils/service/ap-translation.service';
import {Injectable}                                                           from '@angular/core';
import {
  ApDynGridColumnConfigBase,
  ApDynGridColumnHideConfig
}                                                                             from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridEditColumnConfig
}                                                                             from '../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType,
  PropertyTemplate
}                                                                             from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridStatusColumnConfig
}                                                                             from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {MapViewStore}                                                         from '../../stores/layout/mapview.store';
import {MapViewMode}                                                          from '../../ap-interface';
import {map}                                                                  from 'rxjs/operators';
import {
  ApDynGridDetailsGridConfig
}                                                                             from '../../ap-dyngrids/config/details/ap-dyn-grid-details-grid-config';
import {
  ApDynGridGroupColumnConfig
}                                                                             from '../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {BehaviorSubject, of, Subscription}                                    from 'rxjs';
import {nameOf}                                                               from '../../ap-core/utils/ap-name-of';
import {IPpOverviewDetailsGridItem, IPpOverviewGridItem, PpColumnSwitchValue} from '../types/pp-overview.types';
import {
  GetRoundNumericService
}                                                                             from '../../ap-utils/service/get-round-numeric.service';
import {
  ApUtilService
}                                                                             from '../../ap-utils/service/ap-util.service';
import {PpNoDataValue}                                                        from '../types/pp-planning-booking.types';
import {
  ApTooltipContentService
}                                                                             from '../../ap-utils/service/ap-tooltip-content.service';
import {
  IGridStatus
}                                                                             from '../../ap-interface/interfaces/ap-grid-status.interface';

/**
 * Service for grid configuration of plant protection overview
 * Plant-Protection -> overview
 */
@Injectable({
  providedIn: 'root'
})
export class PpOverviewGridConfig {
  private _subscriptions: Array<Subscription> = [];
  private _detailsColumnSwitch$: BehaviorSubject<PpColumnSwitchValue> =
    new BehaviorSubject<PpColumnSwitchValue>(PpColumnSwitchValue.Water);

  constructor(private languageStore: LanguageStore,
              private settingsStore: SettingsStore,
              private roundNumericPipe: GetRoundNumericPipe,
              private mapViewStore: MapViewStore,
              private tooltipContentService: ApTooltipContentService,
              private roundNumericService: GetRoundNumericService,
              private translationService: ApTranslationService) {
  }

  public getMainGridColumns(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridStatusColumnConfig({
        field: `${nameOf<IPpOverviewGridItem>('Status')}.${nameOf<IGridStatus>('state')}`,
        class: `${nameOf<IPpOverviewGridItem>('Status')}.${nameOf<IGridStatus>('class')}`,
        description: `${nameOf<IPpOverviewGridItem>('Status')}.${nameOf<IGridStatus>('description')}`,
        filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__NumberAbbr',
        field: nameOf<IPpOverviewGridItem>('Nr'),
        hide: true,
        width: 40,
        filterable: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__FieldPartShort',
        field: nameOf<IPpOverviewGridItem>('SNr'),
        hide: true,
        width: 40,
        filterable: true,
        filterType: FilterType.NUMBER,
        cssClass: 'right',
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__NumberAbbr',
        field: nameOf<IPpOverviewGridItem>('NrSNr'),
        width: 75,
        filterable: true,
        hide: new ApDynGridColumnHideConfig({
          mapHide: false,
          selfHide: this.mapViewStore.Listen(s => s.mode).pipe(map((m) => m !== MapViewMode.NORMAL)),
        }),
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Name',
        field: nameOf<IPpOverviewGridItem>('Name'),
        cssClass: 'left',
        width: 180,
        filterable: true,
        sortIndex: 0,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Docu_Ini__Area',
        field: nameOf<IPpOverviewGridItem>('Area'),
        width: 60,
        filterable: true,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        template: PropertyTemplate.NUMBER,
        headerFilterable: true,
        tooltip: (dataItem: IPpOverviewGridItem) => this.roundNumericService.stringify(dataItem.Area, 4),
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Crop',
        field: nameOf<IPpOverviewGridItem>('Crop'),
        width: 100,
        filterable: true,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__ECStageShort',
        field: nameOf<IPpOverviewGridItem>('EcValue'),
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        filterable: true,
        width: 40
      }),
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Fieldcrop_Notes',
        field: nameOf<IPpOverviewGridItem>('CropNote'),
        tooltip: (dataItem: IPpOverviewGridItem) => this.tooltipContentService.generateCropNoteTooltip(dataItem?.CropNote),
        width: 100
      })
    ];
  }

  public getDetailsGridColumns(onShowPerPlanView: (value: IPpOverviewDetailsGridItem) => void,
                               onShowPerFieldView: (value: IPpOverviewDetailsGridItem) => void): ApDynGridDetailsGridConfig {
    return new ApDynGridDetailsGridConfig({
      field: 'Applications',
      checkboxDisabled: true,
      hide: (dataItem) => !(dataItem?.Applications?.length > 0),
      columns: [
        new ApDynGridStatusColumnConfig({
          field: `${nameOf<IPpOverviewDetailsGridItem>('Status')}.${nameOf<IGridStatus>('state')}`,
          class: `${nameOf<IPpOverviewDetailsGridItem>('Status')}.${nameOf<IGridStatus>('class')}`,
          description: `${nameOf<IPpOverviewDetailsGridItem>('Status')}.${nameOf<IGridStatus>('description')}`,
          filterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global_Application_Date',
          field: nameOf<IPpOverviewDetailsGridItem>('ApplDate'),
          format: this.languageStore.CurrentDateFormat,
          sortIndex: 0,
          hide: true,
          sortDesc: true,
          filterable: true,
          width: 80
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global__ECStageShort',
          field: nameOf<IPpOverviewDetailsGridItem>('Ec'),
          cssClass: 'right',
          filterType: FilterType.NUMBER,
          filterable: true,
          width: 40
        }),
        new ApDynGridGroupColumnConfig({
          title: 'N__ChartTitleX',
          width: 150,
          hide: true,
          groupColumns: [
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Avg_Symbol',
              field: nameOf<IPpOverviewDetailsGridItem>('NUptakeAvg'),
              width: 50,
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Min',
              field: nameOf<IPpOverviewDetailsGridItem>('NUptakeMin'),
              width: 50,
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Max',
              field: nameOf<IPpOverviewDetailsGridItem>('NUptakeMax'),
              width: 50,
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
          ]
        }),
        new ApDynGridGroupColumnConfig({
          title: `${this.translationService.translate('Global__Water')} [${this.translationService.translate('Unit_l_per_ha')}]`,
          width: 150,
          hide: true,
          groupColumns: [
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Avg_Symbol',
              field: nameOf<IPpOverviewDetailsGridItem>('WaterAvg'),
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              width: 50,
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Min',
              field: nameOf<IPpOverviewDetailsGridItem>('WaterMin'),
              width: 50,
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Max',
              field: nameOf<IPpOverviewDetailsGridItem>('WaterMax'),
              width: 50,
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
          ]
        }),
        new ApDynGridGroupColumnConfig({
          title: this._detailsColumnSwitch$.pipe(map((c) => c === PpColumnSwitchValue.Water ?
            `${this.translationService.translate('Global__Water')} [${this.translationService.translate('Unit_l_per_ha')}]` :
            'N__ChartTitleX')),
          hide: new ApDynGridColumnHideConfig({
            mapHide: false,
            selfHide: this.mapViewStore.Listen(s => s.mode).pipe(map((m) => m !== MapViewMode.NORMAL)),
          }),
          xlsExport: false,
          width: 150,
          headerButtons: {
            nextClicked: ApUtilService.createEventEmitter(this.switchColumn.bind(this), this._subscriptions),
            previousClicked: ApUtilService.createEventEmitter(this.switchColumn.bind(this), this._subscriptions),
            nextDisabled: of(false),
            previousDisabled: of(false)
          },
          groupColumns: [
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Avg_Symbol',
              field: nameOf<IPpOverviewDetailsGridItem>('NUptakeAvg'),
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              width: 50,
              hide: new ApDynGridColumnHideConfig({
                mapHide: false,
                selfHide: this._detailsColumnSwitch$.pipe(map((c) => c !== PpColumnSwitchValue.NUptake))
              }),
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Min',
              field: nameOf<IPpOverviewDetailsGridItem>('NUptakeMin'),
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              width: 50,
              hide: new ApDynGridColumnHideConfig({
                mapHide: false,
                selfHide: this._detailsColumnSwitch$.pipe(map((c) => c !== PpColumnSwitchValue.NUptake))
              }),
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Max',
              field: nameOf<IPpOverviewDetailsGridItem>('NUptakeMax'),
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              width: 50,
              hide: new ApDynGridColumnHideConfig({
                mapHide: false,
                selfHide: this._detailsColumnSwitch$.pipe(map((c) => c !== PpColumnSwitchValue.NUptake))
              }),
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Avg_Symbol',
              field: nameOf<IPpOverviewDetailsGridItem>('WaterAvg'),
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              width: 50,
              hide: new ApDynGridColumnHideConfig({
                mapHide: false,
                selfHide: this._detailsColumnSwitch$.pipe(map((c) => c !== PpColumnSwitchValue.Water))
              }),
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Min',
              field: nameOf<IPpOverviewDetailsGridItem>('WaterMin'),
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              width: 50,
              hide: new ApDynGridColumnHideConfig({
                mapHide: false,
                selfHide: this._detailsColumnSwitch$.pipe(map((c) => c !== PpColumnSwitchValue.Water))
              }),
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            }),
            new ApDynGridPropertyColumnConfig({
              title: 'Global__Max',
              field: nameOf<IPpOverviewDetailsGridItem>('WaterMax'),
              cssClass: 'right',
              filterType: FilterType.NUMBER,
              filterable: true,
              width: 50,
              hide: new ApDynGridColumnHideConfig({
                mapHide: false,
                selfHide: this._detailsColumnSwitch$.pipe(map((c) => c !== PpColumnSwitchValue.Water))
              }),
              pipes: [{
                pipe: this.roundNumericPipe,
                args: [0, {ToReplace: [PpNoDataValue], ReplaceWith: ''}]
              }]
            })
          ]
        }),
        new ApDynGridPropertyColumnConfig({
          title: `${this.translationService.translate('Global_Worked_Area')} [${this.translationService.translate('Unit_ha')}]`,
          breakHeaderLine: true,
          field: nameOf<IPpOverviewDetailsGridItem>('WorkArea'),
          filterable: true,
          width: 78,
          hide: true,
          cssClass: 'right',
          filterType: FilterType.NUMBER,
          template: PropertyTemplate.NUMBER,
          tooltip: (dataItem: IPpOverviewDetailsGridItem) => this.roundNumericService.stringify(dataItem.WorkArea, 4),
          pipes: [{
            pipe: this.roundNumericPipe,
            args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
          }]
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global__Products',
          field: nameOf<IPpOverviewDetailsGridItem>('Products'),
          width: 400,
          hide: true,
          filterable: true,
          headerFilterable: true,
          tooltip: (dataItem: IPpOverviewDetailsGridItem) => dataItem?.ProductsTooltip
        }),
        new ApDynGridPropertyColumnConfig({
          title: 'Global__Products',
          field: nameOf<IPpOverviewDetailsGridItem>('Products'),
          width: 120,
          filterable: true,
          headerFilterable: true,
          tooltip: (dataItem: IPpOverviewDetailsGridItem) => dataItem?.ProductsTooltip,
          hide: new ApDynGridColumnHideConfig({
            mapHide: false,
            selfHide: this.mapViewStore.Listen(s => s.mode).pipe(map((m) => m !== MapViewMode.NORMAL)),
          }),
        }),
        new ApDynGridEditColumnConfig({
          data: [
            {
              text: 'ShowMapPpOverviewPerPlan',
              callback: ApUtilService.createEventEmitter(onShowPerPlanView, this._subscriptions),
            },
            {
              text: 'Global__SingleFieldView',
              callback: ApUtilService.createEventEmitter(onShowPerFieldView, this._subscriptions),
            }
          ]
        })
      ]
    });
  }

  public destroy(): void {
    this._subscriptions.forEach(d => d.unsubscribe());
  }

  private switchColumn(): void {
    this._detailsColumnSwitch$.next(this._detailsColumnSwitch$.value === PpColumnSwitchValue.NUptake ?
      PpColumnSwitchValue.Water : PpColumnSwitchValue.NUptake);
  }
}
